/**
 * QuickActions Component
 *
 * Mobile-optimized action buttons for purchase decisions.
 * Three main actions aligned with traffic light colors:
 * - Take Control (Green) - Approve and proceed
 * - Think It Over (Blue) - Delay and set reminder
 * - Sleep On It (Red) - Decline/postpone
 *
 * Features:
 * - Large touch targets (min 48px)
 * - Haptic feedback
 * - Swipe gesture support
 * - Game-like visual feedback
 */

import { useState, useCallback, useRef } from 'react';
import { Box, Button, Typography, keyframes, Zoom, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import NightsStayIcon from '@mui/icons-material/NightsStay';

export type PurchaseAction = 'approve' | 'delay' | 'decline';

export interface QuickActionsProps {
  onAction: (action: PurchaseAction) => Promise<void> | void;
  isLoading?: boolean;
  disabled?: boolean;
  recommendedAction?: PurchaseAction;
}

// Bounce animation for selected action
const bounceAnimation = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
`;

// Ripple animation
const rippleAnimation = keyframes`
  0% {
    transform: scale(0);
    opacity: 0.5;
  }
  100% {
    transform: scale(2.5);
    opacity: 0;
  }
`;

// Shake animation for incorrect/warning
const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
`;

// Action configuration
const actionConfig = {
  approve: {
    label: 'Take Control',
    shortLabel: 'Go',
    icon: CheckCircleOutlineIcon,
    color: '#10B981',
    hoverColor: '#059669',
    bgGradient: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
    hapticPattern: [50, 30, 50],
    emoji: '✅',
  },
  delay: {
    label: 'Think It Over',
    shortLabel: 'Wait',
    icon: ScheduleIcon,
    color: '#3B82F6',
    hoverColor: '#2563EB',
    bgGradient: 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
    hapticPattern: [100, 50, 100],
    emoji: '🤔',
  },
  decline: {
    label: 'Sleep On It',
    shortLabel: 'No',
    icon: NightsStayIcon,
    color: '#EF4444',
    hoverColor: '#DC2626',
    bgGradient: 'linear-gradient(135deg, #DC2626 0%, #EF4444 100%)',
    hapticPattern: [200],
    emoji: '🌙',
  },
};

interface ActionButtonProps {
  action: PurchaseAction;
  onClick: () => void;
  isLoading: boolean;
  isSelected: boolean;
  disabled: boolean;
  isRecommended: boolean;
  compact?: boolean;
}

function ActionButton({
  action,
  onClick,
  isLoading,
  isSelected,
  disabled,
  isRecommended,
  compact = false,
}: ActionButtonProps) {
  const config = actionConfig[action];
  const Icon = config.icon;
  const [showRipple, setShowRipple] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = useCallback(() => {
    if (disabled || isLoading) return;

    // Trigger haptic feedback
    if ('vibrate' in navigator) {
      try {
        navigator.vibrate(config.hapticPattern);
      } catch {
        // Haptic not available
      }
    }

    // Show ripple effect
    setShowRipple(true);
    setTimeout(() => setShowRipple(false), 400);

    onClick();
  }, [onClick, disabled, isLoading, config.hapticPattern]);

  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        minWidth: compact ? 80 : 100,
      }}
    >
      {/* Recommended badge */}
      {isRecommended && !isLoading && (
        <Zoom in>
          <Box
            sx={{
              position: 'absolute',
              top: -10,
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: config.color,
              color: 'white',
              px: 1.5,
              py: 0.25,
              borderRadius: 10,
              fontSize: '0.625rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              zIndex: 1,
              boxShadow: `0 2px 8px ${config.color}40`,
            }}
          >
            Best Choice
          </Box>
        </Zoom>
      )}

      <Button
        ref={buttonRef}
        onClick={handleClick}
        disabled={disabled || isLoading}
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: compact ? 64 : 80,
          py: compact ? 1.5 : 2,
          px: compact ? 1 : 2,
          borderRadius: 3,
          background: isSelected ? config.bgGradient : 'transparent',
          border: `2px solid ${config.color}`,
          color: isSelected ? 'white' : config.color,
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          animation: isSelected ? `${bounceAnimation} 0.5s ease` : 'none',

          '&:hover': {
            background: config.bgGradient,
            color: 'white',
            transform: 'translateY(-2px)',
            boxShadow: `0 8px 24px ${config.color}40`,
          },

          '&:active': {
            transform: 'translateY(0) scale(0.98)',
          },

          '&:disabled': {
            opacity: 0.5,
            transform: 'none',
          },

          // Touch-friendly tap highlight
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
        }}
      >
        {/* Ripple effect */}
        {showRipple && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              transform: 'translate(-50%, -50%)',
              animation: `${rippleAnimation} 0.4s ease-out`,
            }}
          />
        )}

        {/* Button content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {isLoading && isSelected ? (
            <CircularProgress size={compact ? 24 : 32} sx={{ color: 'inherit' }} />
          ) : (
            <>
              <Icon sx={{ fontSize: compact ? 28 : 36 }} />
              <Typography
                variant={compact ? 'caption' : 'body2'}
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  lineHeight: 1.2,
                }}
              >
                {compact ? config.shortLabel : config.label}
              </Typography>
            </>
          )}
        </Box>
      </Button>
    </Box>
  );
}

export function QuickActions({
  onAction,
  isLoading = false,
  disabled = false,
  recommendedAction,
}: QuickActionsProps) {
  const [selectedAction, setSelectedAction] = useState<PurchaseAction | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);

  const handleAction = useCallback(
    async (action: PurchaseAction) => {
      setSelectedAction(action);
      try {
        await onAction(action);
      } catch {
        // Shake animation on error
        if (containerRef.current) {
          containerRef.current.style.animation = `${shakeAnimation} 0.5s ease`;
          setTimeout(() => {
            if (containerRef.current) {
              containerRef.current.style.animation = '';
            }
          }, 500);
        }
      }
    },
    [onAction]
  );

  // Swipe gesture handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null || disabled || isLoading) return;

      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchEndX - touchStartX.current;
      const threshold = 80; // Minimum swipe distance

      if (Math.abs(diff) >= threshold) {
        if (diff > 0) {
          // Swipe right = approve
          handleAction('approve');
        } else {
          // Swipe left = decline
          handleAction('decline');
        }
      }

      touchStartX.current = null;
    },
    [disabled, isLoading, handleAction]
  );

  return (
    <Box
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      sx={{
        width: '100%',
        userSelect: 'none',
      }}
    >
      {/* Swipe hint */}
      <Typography
        variant="caption"
        sx={{
          display: { xs: 'block', sm: 'none' },
          textAlign: 'center',
          color: 'text.secondary',
          mb: 2,
          opacity: 0.7,
        }}
      >
        Swipe right to approve, left to decline
      </Typography>

      {/* Action buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'center',
          flexWrap: { xs: 'nowrap', sm: 'nowrap' },
        }}
      >
        {(['decline', 'delay', 'approve'] as PurchaseAction[]).map((action) => (
          <ActionButton
            key={action}
            action={action}
            onClick={() => handleAction(action)}
            isLoading={isLoading}
            isSelected={selectedAction === action}
            disabled={disabled}
            isRecommended={recommendedAction === action}
          />
        ))}
      </Box>

      {/* Keyboard shortcuts hint for desktop */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          justifyContent: 'center',
          gap: 4,
          mt: 2,
        }}
      >
        {[
          { key: 'D', action: 'decline', label: 'Decline' },
          { key: 'W', action: 'delay', label: 'Wait' },
          { key: 'A', action: 'approve', label: 'Approve' },
        ].map(({ key, action, label }) => (
          <Typography
            key={action}
            variant="caption"
            sx={{
              color: 'text.disabled',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Box
              component="kbd"
              sx={{
                px: 0.75,
                py: 0.25,
                borderRadius: 0.5,
                backgroundColor: 'action.hover',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
              }}
            >
              {key}
            </Box>
            {label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

export default QuickActions;
